import { Box, Flex, Icon, keyframes, Progress, Text } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import {
  HiOutlineCheckCircle,
  HiOutlineDocumentText,
  HiOutlineExclamationTriangle,
  HiOutlineTrash,
} from "react-icons/hi2";

import { CATEGORY_ICONS, DEFAULT_CATEGORY_ICON } from "./categoryIcons";
import type {
  DocumentAnalysis,
  DocumentUploadPhase,
} from "./useDocumentUpload";

const FILE_TYPE_ICONS: Record<string, React.ElementType> = {
  "application/pdf": HiOutlineDocumentText,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    HiOutlineDocumentText,
  "text/plain": HiOutlineDocumentText,
  "text/html": HiOutlineDocumentText,
  "text/markdown": HiOutlineDocumentText,
  "application/rtf": HiOutlineDocumentText,
};

const CATEGORY_LABELS: Record<string, string> = {
  JOB_DESCRIPTION: "Job Description",
  CURRENT_INTERVIEW_PROCESS: "Interview Process",
  IDEAL_CANDIDATE_PROFILE: "Candidate Profile",
  CULTURE_AND_TEAM_CONTEXT: "Culture & Team",
  PAST_INTERVIEW_MATERIAL: "Past Interview",
  NONE_OF_THE_ABOVE: "Other Document",
};

const DOC_TYPE_LABELS: Record<string, string> = {
  JOB_DESCRIPTION: "Job Description",
  INTERVIEW_GUIDES: "Interview Guide",
  SCORECARDS: "Scorecard",
  EVALUATION_RUBRICS: "Evaluation Rubric",
  IDEAL_CANDIDATE_DOCUMENTS: "Ideal Candidate",
  INTAKE_NOTES: "Intake Notes",
  LEVELING_EXPECTATIONS: "Leveling",
  COMPANY_PRINCIPLES: "Company Principles",
  CULTURE_DOCS: "Culture Doc",
  INTERVIEW_RECORDINGS: "Interview Recording",
  INTAKE_MEETINGS: "Intake Meeting",
  DEBRIEF_SESSIONS: "Debrief Session",
  CALIBRATION_SESSIONS: "Calibration",
  UNKNOWN: "Document",
};

const PHASE_LABELS: Record<DocumentUploadPhase, string> = {
  idle: "Waiting to start...",
  uploading: "Uploading...",
  processing: "Processing...",
  reviewing: "Analyzing...",
  complete: "Complete",
  error: "Error",
};

function getFileExtension(fileName: string): string {
  const parts = fileName.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : "";
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// --- Animations ---

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const borderGlow = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

const particleBurst = keyframes`
  0% { transform: scale(0.8); opacity: 0; }
  30% { transform: scale(1.05); opacity: 1; }
  60% { transform: scale(1); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const sparkle = keyframes`
  0% { transform: scale(0) rotate(0deg); opacity: 0; }
  50% { transform: scale(1.2) rotate(180deg); opacity: 1; }
  100% { transform: scale(0) rotate(360deg); opacity: 0; }
`;

const checkIn = keyframes`
  0% { transform: scale(0); opacity: 0; }
  60% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

// --- Sparkle Particle ---

const SparkleParticle: React.FC<{
  top: string;
  left: string;
  delay: string;
  size: string;
}> = ({ top, left, delay, size }) => (
  <Box
    position="absolute"
    top={top}
    left={left}
    w={size}
    h={size}
    borderRadius="full"
    bg="green.300"
    animation={`${sparkle} 0.8s ${delay} ease-out forwards`}
    opacity={0}
    pointerEvents="none"
  />
);

// --- Component ---

type DocumentUploadCardProps = {
  phase: DocumentUploadPhase;
  fileName: string;
  fileMimeType: string;
  fileSizeBytes: number;
  uploadProgress: number;
  analysis: DocumentAnalysis | null;
  errorMessage: string | null;
  onRemove?: () => void;
};

export const DocumentUploadCard: React.FC<DocumentUploadCardProps> = ({
  phase,
  fileName,
  fileMimeType,
  fileSizeBytes,
  uploadProgress,
  analysis,
  errorMessage,
  onRemove,
}) => {
  const [showParticles, setShowParticles] = useState(false);
  const [prevPhase, setPrevPhase] = useState<DocumentUploadPhase>(phase);

  useEffect(() => {
    if (phase === "complete" && prevPhase !== "complete") {
      setShowParticles(true);
      const timer = setTimeout(() => setShowParticles(false), 1200);
      return () => clearTimeout(timer);
    }
    setPrevPhase(phase);
  }, [phase, prevPhase]);

  const isAsync =
    phase === "idle" ||
    phase === "uploading" ||
    phase === "processing" ||
    phase === "reviewing";
  const isComplete = phase === "complete";
  const isError = phase === "error";

  const FileIcon = FILE_TYPE_ICONS[fileMimeType] || HiOutlineDocumentText;
  const ext = getFileExtension(fileName);

  const CategoryIcon = analysis
    ? CATEGORY_ICONS[analysis.category] || DEFAULT_CATEGORY_ICON
    : null;
  const categoryLabel = analysis
    ? CATEGORY_LABELS[analysis.category] || analysis.category
    : null;
  const docTypeLabel = analysis
    ? DOC_TYPE_LABELS[analysis.documentType] || analysis.documentType
    : null;

  const borderColor = isComplete
    ? "green.400"
    : isError
    ? "red.400"
    : "blue.200";

  // Each phase owns 33%: uploading 0-33, processing 33-66, reviewing 66-100, complete 100
  const PHASE_BASE: Record<DocumentUploadPhase, number> = {
    idle: 0,
    uploading: 0,
    processing: 33,
    reviewing: 66,
    complete: 100,
    error: 0,
  };

  const [creepOffset, setCreepOffset] = useState(0);
  const creepIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setCreepOffset(0);
    if (creepIntervalRef.current) {
      clearInterval(creepIntervalRef.current);
      creepIntervalRef.current = null;
    }

    if (phase === "processing" || phase === "reviewing") {
      creepIntervalRef.current = setInterval(() => {
        setCreepOffset((prev) => Math.min(prev + 1, 24));
      }, 1000);
    }

    return () => {
      if (creepIntervalRef.current) {
        clearInterval(creepIntervalRef.current);
      }
    };
  }, [phase]);

  let progressPercent: number;
  if (phase === "uploading") {
    progressPercent = uploadProgress * 33;
  } else if (phase === "processing" || phase === "reviewing") {
    progressPercent = PHASE_BASE[phase] + creepOffset;
  } else if (isComplete) {
    progressPercent = 100;
  } else {
    progressPercent = 0;
  }

  return (
    <Box
      position="relative"
      borderRadius="12px"
      overflow="hidden"
      transition="all 0.3s ease"
      animation={isComplete ? `${particleBurst} 0.5s ease-out` : undefined}
    >
      {/* Animated border overlay for async states — 1px to match end state */}
      {isAsync && (
        <Box
          position="absolute"
          inset="-1px"
          borderRadius="13px"
          border="1px solid transparent"
          bgGradient="linear(90deg, #0074DD, #591EFF, #9643FF, #0074DD)"
          bgSize="200% 100%"
          animation={`${shimmer} 2s linear infinite, ${borderGlow} 2s ease-in-out infinite`}
          sx={{
            WebkitMask:
              "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
            padding: "1px",
          }}
          pointerEvents="none"
          zIndex={1}
        />
      )}

      {/* Completion particles */}
      {showParticles && (
        <Box
          position="absolute"
          inset="0"
          zIndex={2}
          pointerEvents="none"
          overflow="visible"
        >
          <SparkleParticle top="-4px" left="20%" delay="0s" size="6px" />
          <SparkleParticle top="10%" left="-4px" delay="0.1s" size="4px" />
          <SparkleParticle top="-4px" left="60%" delay="0.15s" size="5px" />
          <SparkleParticle
            top="50%"
            left="calc(100% + 2px)"
            delay="0.2s"
            size="4px"
          />
          <SparkleParticle
            top="calc(100% + 2px)"
            left="40%"
            delay="0.1s"
            size="6px"
          />
          <SparkleParticle
            top="calc(100% + 2px)"
            left="80%"
            delay="0.25s"
            size="4px"
          />
          <SparkleParticle top="30%" left="-4px" delay="0.3s" size="5px" />
          <SparkleParticle top="-4px" left="85%" delay="0.05s" size="4px" />
        </Box>
      )}

      <Box
        bg="white"
        border="1px solid"
        borderColor={borderColor}
        borderRadius="12px"
        px="4"
        py="3"
        position="relative"
        zIndex={0}
        transition="all 0.4s ease"
      >
        <Flex align="flex-start" gap="3">
          {/* File type icon / status icon — anchored top-left */}
          <Flex
            align="center"
            justify="center"
            w="10"
            h="10"
            borderRadius="lg"
            bg={isComplete ? "green.100" : isError ? "red.100" : "gray.100"}
            flexShrink={0}
            mt="0.5"
            transition="all 0.3s ease"
          >
            {isComplete && CategoryIcon ? (
              <Icon
                as={CategoryIcon}
                boxSize="5"
                color="green.600"
                animation={`${checkIn} 0.4s ease-out`}
              />
            ) : isError ? (
              <Icon
                as={HiOutlineExclamationTriangle}
                boxSize="5"
                color="red.500"
              />
            ) : (
              <Icon as={FileIcon} boxSize="5" color="gray.500" />
            )}
          </Flex>

          {/* Content area */}
          <Box flex="1" minW="0">
            <Flex align="center" gap="2" mb="0.5">
              <Text
                fontSize="sm"
                fontWeight="semibold"
                color={
                  isComplete ? "green.800" : isError ? "red.800" : "gray.800"
                }
                noOfLines={1}
              >
                {fileName}
              </Text>
              {ext && (
                <Text
                  fontSize="2xs"
                  fontWeight="bold"
                  color={isComplete ? "green.600" : "gray.600"}
                  textTransform="uppercase"
                  flexShrink={0}
                  bg={isComplete ? "green.100" : "gray.100"}
                  px="1.5"
                  py="0.5"
                  borderRadius="sm"
                  lineHeight="1"
                >
                  {ext}
                </Text>
              )}
            </Flex>

            {isAsync && (
              <Box>
                <Flex align="center" justify="space-between" mb="1">
                  <Text fontSize="xs" color="gray.500">
                    {PHASE_LABELS[phase]}
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    {formatFileSize(fileSizeBytes)}
                  </Text>
                </Flex>
                <Progress
                  value={progressPercent}
                  size="xs"
                  borderRadius="full"
                  bg="gray.100"
                  sx={{
                    "& > div": {
                      bgGradient: "linear(to-r, #0074DD, #591EFF, #9643FF)",
                      transition: "width 0.6s ease",
                    },
                  }}
                />
              </Box>
            )}

            {isComplete && analysis && (
              <Flex align="center" gap="2" flexWrap="wrap">
                <Flex
                  align="center"
                  gap="1"
                  bg="green.100"
                  px="2"
                  py="0.5"
                  borderRadius="full"
                >
                  {CategoryIcon && (
                    <Icon as={CategoryIcon} boxSize="3" color="green.600" />
                  )}
                  <Text fontSize="2xs" fontWeight="semibold" color="green.700">
                    {categoryLabel}
                  </Text>
                </Flex>
                {categoryLabel !== docTypeLabel && (
                  <Text fontSize="2xs" color="gray.400">
                    {docTypeLabel}
                  </Text>
                )}
              </Flex>
            )}

            {isComplete && !analysis && (
              <Flex align="center" gap="1">
                <Icon
                  as={HiOutlineCheckCircle}
                  boxSize="3.5"
                  color="green.500"
                />
                <Text fontSize="xs" color="green.600" fontWeight="medium">
                  Document processed
                </Text>
              </Flex>
            )}

            {isError && (
              <Text fontSize="xs" color="red.600">
                {errorMessage || "An error occurred"}
              </Text>
            )}
          </Box>

          {/* Trash icon — top-right */}
          {onRemove && (
            <Flex
              as="button"
              align="center"
              justify="center"
              w="7"
              h="7"
              flexShrink={0}
              mt="0.5"
              borderRadius="md"
              color="gray.400"
              cursor="pointer"
              transition="all 0.15s"
              _hover={{ color: "red.500", bg: "red.50" }}
              onClick={onRemove}
              aria-label={`Remove ${fileName}`}
            >
              <Icon as={HiOutlineTrash} boxSize="4" />
            </Flex>
          )}
        </Flex>
      </Box>
    </Box>
  );
};
